import { dispatch, store } from '../../store';
import { IApiQueryListResponse, IApiQuerySingularResponse, typedApiFetch } from '../../utils';
import {
  creditNotesListReceived,
  currentCreditNoteCleared,
  currentCreditNoteReceived,
  setLoadedState,
} from './creditNotesSlice';
import { ICreditNote } from '../../../interfaces/ICreditNote';
import { REACT_APP_API_ROOT_URI } from '../../../App';
import { performApiActionRequest, TActionResponse } from '../../apiActionRequest';
import { arrayWrap, chunkArray, Hash, mapAsyncSequential } from '@jamesgmarks/utilities';
import { showMessage } from '../messaging/actions';
import { IApiCommandResponse, IApiQueryResponse } from '@llws/api-common';
import { EmailData } from '../../../interfaces/EmailData';
import { IApplyCreditNoteModalSubmitData } from '../../../interfaces/IApplyCreditModalSubmitData';
import { EHydraCreditNoteState } from '../../../entity-interfaces/EHydraCreditNoteState';
import { loadCurrentInvoice, loadCurrentWIPInvoice } from '../invoices/actions';
import { loadAvailableCreditData } from '../credits/actions';
import { IPayment } from '../payments/interfaces';
import { ICredit } from '../../../interfaces/ICredit';
import {
  IMassCreditCreateResponse,
  IMassCreditPreviewResponse,
  MASS_CREDIT_UPLOAD_FIELD,
} from '../../../interfaces/IMassCreditNotes';
import { ICreditNoteSplit } from '../../../components/pages/Credits/BulkSubscriptionCredit/types';
import { makeYmd } from 'src/app-utils';
import { CreditNote } from '../../../../../entities/hydra';

export const loadCurrentCreditNote = async (
  {
    creditNoteId,
  }: {
    creditNoteId: number
  },
) => {
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes/${creditNoteId}`;
  dispatch(setLoadedState('loading'));
  const response = await typedApiFetch(url);
  const responseData = await response.json() as IApiQuerySingularResponse<ICreditNote>;
  dispatch(currentCreditNoteReceived(responseData));
};

export const clearCurrentCreditNote = async () => {
  dispatch(currentCreditNoteCleared());
};

export const loadCreditNotes = async ({ billingAccountId }: { billingAccountId?: number }) => {
  const qs = billingAccountId ? `?billingAccountId=${billingAccountId}` : '';
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes${qs}`;
  dispatch(setLoadedState('loading'));
  const response = await typedApiFetch(url);
  const responseData = await response.json() as IApiQueryListResponse<ICreditNote>;
  dispatch(creditNotesListReceived(responseData));
};

export const saveCreditNote = async (creditNote: ICreditNote) => {
  dispatch(setLoadedState('loading'));
  const suffix = creditNote.id ? `/${creditNote.id}` : '';
  const method = creditNote.id ? 'PUT' : 'POST';
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes${suffix}`;

  const reducedCreditNote = {
    ...creditNote,
    credits: creditNote.credits.map((credit: ICredit) => {
      const { generatedSubscription, rawJson, ...reducedCredit } = credit;
      return reducedCredit;
    }),
  };

  const response = await typedApiFetch<IApiCommandResponse<ICreditNote>>(url, {
    method,
    body: JSON.stringify(reducedCreditNote),
  });

  const { data } = await response.json();

  const [savedCreditNote] = arrayWrap(data);

  if (savedCreditNote.id) {
    loadCurrentCreditNote({ creditNoteId: savedCreditNote.id });
  }

  loadCreditNotes({});

  return savedCreditNote;
};

/**
 * Credit note IDs sent per request. The server queues everything it is given, so this exists to
 * keep any one request sane: each ID becomes a bound parameter in a single `IN (...)` query, and
 * the whole list travels as one JSON body. Chunking bounds both regardless of selection size.
 */
export const PDF_GENERATION_CHUNK_SIZE = 250;

/**
 * Performs the `generatePdfsForCreditNotes()` action and then alerts the user.
 *
 * Large selections are split across several requests, dispatched in order. The server appends each
 * batch to one shared queue and renders sequentially, so the split does not affect ordering or
 * throughput — the work is queued in the same sequence either way.
 *
 * @param creditNoteIds - An array of credit note IDs to trigger PDF generation for.
 */
export const triggerCreditNotePdfGeneration = async (
  creditNoteIds: number[],
): Promise<void> => {
  const creditNoteIdChunks = chunkArray(creditNoteIds, PDF_GENERATION_CHUNK_SIZE);

  await mapAsyncSequential(
    creditNoteIdChunks,
    async (creditNoteIdChunk: number[]) => (
      await performApiActionRequest<TActionResponse<Hash>>(
        'generatePdfsForCreditNotes',
        { creditNoteIds: creditNoteIdChunk, waitForQueueToFinish: false },
      ) as IApiQueryResponse<TActionResponse<Hash>>
    ),
  );

  showMessage({
    message: `Credit Notes PDF generation started for ${creditNoteIds.length} credit note(s)...`,
    severity: 'success',
  });
};

interface ICreditNoteNotification {
  toBillingContactIds: number[];
  creditNoteIds: number[];
}

const isCreditNoteNotification = (obj: unknown): obj is ICreditNoteNotification => (
  !!(obj as ICreditNoteNotification).creditNoteIds
);

export const assertValidCreditNoteNotificationArray = (x: unknown[]): ICreditNoteNotification[] | never => {
  if (!x.every(isCreditNoteNotification)) {
    throw new Error('Invalid array! Every element must be `ICreditNoteNotification`!');
  }

  return x;
};

export const sendMultipleCreditNoteNotifications = async (
  notifications: ICreditNoteNotification[],
) => {
  const systemInfo = store.getState().systemInfo;

  dispatch(setLoadedState('loading'));

  const outNotifications = notifications.reduce((acc, n) => {
    if (!n.toBillingContactIds || n.toBillingContactIds.length === 0) {
      showMessage({ message: 'Must select billing contacts.' });
      throw new Error('Must select billing contacts');
    }

    return [
      ...acc,
      ...(n.toBillingContactIds.map(c => ({
        to: c,
        creditNoteIds: n.creditNoteIds,
        isDevelopment: systemInfo.env.NODE_ENV !== 'production',
      }))),
    ];
  }, [] as ({ to: number, creditNoteIds: number[], isDevelopment: boolean; })[]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const response = await performApiActionRequest<TActionResponse<{ to: EmailData, success: boolean; }[]>>(
    'triggerCreditNoteNotifications',
    { notifications: outNotifications },
  );

  dispatch(setLoadedState('creditNotesSent'));
  showMessage({ message: 'Credit Notes sent', severity: 'success' });

  setTimeout(() => { setLoadedState('idle'); }, 5000);
};

export const applyCreditNote = async (creditNoteApplyData: IApplyCreditNoteModalSubmitData) => {
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes/applied`;

  dispatch(setLoadedState('loading'));

  const response = await typedApiFetch<IApiQuerySingularResponse<ICreditNote>>(url, {
    method: 'POST',
    body: JSON.stringify(creditNoteApplyData),
  });

  const responseData = await response.json();
  console.info({ status: response.status, responseData });
  dispatch(currentCreditNoteReceived(responseData)); // TODO: Error handling?

  const { freshbooksClientId, invoiceId, currencyCode } = creditNoteApplyData;

  if (invoiceId) {
    await loadCurrentInvoice({ invoiceId, forceRefresh: true });
    await loadCurrentWIPInvoice({ invoiceId, forceRefresh: true });
  }

  if (freshbooksClientId) {
    await loadCreditNotes({ billingAccountId: freshbooksClientId });
    await loadAvailableCreditData({ freshbooksClientId, currencyCode });
  }
};

export const applyCreditBalanceToSourceInvoices = async (creditNoteId: number) => {
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes/apply_to_source_invoices`;

  dispatch(setLoadedState('loading'));

  const response = (
    await typedApiFetch<IApiCommandResponse<IPayment>>(
      url,
      {
        method: 'POST',
        body: JSON.stringify({ creditNoteId }),
      },
    )
  );

  const createdPayments = arrayWrap((await response.json()).data);

  dispatch(setLoadedState('loaded'));

  if (createdPayments.length > 0) {
    showMessage({
      message: (
        `${createdPayments.length} payment${createdPayments.length > 1 ? 's' : ''}`
        + ` created for the credits of Credit Note ID ${creditNoteId}`
      ),
      severity: 'success',
    });
  }
};

/**
 * Get credit note splits showing how credits will be grouped
 * @param credits - Array of credits to analyze for splitting
 */
export const getCreditNoteSplits = async (credits: ICredit[]): Promise<ICreditNoteSplit[]> => {
  try {
    dispatch(setLoadedState('loading'));

    const url = `${REACT_APP_API_ROOT_URI}/credit_notes/credit_note_splits`;

    const response = await typedApiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credits }),
    });

    if (!response.ok) {
      const errorResult = await response.json() as { error?: string };
      throw new Error(errorResult.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json() as { data: ICreditNoteSplit[] };
    dispatch(setLoadedState('loaded'));

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error('Invalid response format from server');
    }

    return result.data;
  } catch (error) {
    dispatch(setLoadedState('error'));
    throw error;
  }
};

/**
 * Create bulk subscription credits by first getting splits and then creating credit notes
 * @param data - Object containing credits array or client/subscription info
 */
export const createBulkSubscriptionCredits = async (data: {
  clientId?: number;
  subscriptionIds?: number[];
  credits?: ICredit[];
}): Promise<ICreditNoteSplit[]> => {
  try {
    dispatch(setLoadedState('loading'));

    if (!data.credits || data.credits.length === 0) {
      throw new Error('No credits provided for bulk creation');
    }

    // Get the credit note splits to see how credits will be grouped
    const splits = await getCreditNoteSplits(data.credits);

    dispatch(setLoadedState('loaded'));

    // Return the splits for review instead of creating credit notes immediately
    return splits || [];

  } catch (error) {
    dispatch(setLoadedState('error'));
    console.error('Failed to get credit note splits:', error);
    throw error;
  }
};

/**
 * Actually create the credit notes from the reviewed splits
 * @param splits - Array of credit note splits to create
 */
export const createCreditNotesFromSplits = async (splits: ICreditNoteSplit[]) => {
  try {
    dispatch(setLoadedState('loading'));

    const createdCreditNotes: ICreditNote[] = [];

    // Create a credit note for each split
    for (const split of splits) {
      // Get the billing account ID from the first credit's freshbooksClientId
      const billingAccountId = split.credits[0]?.freshbooksClientId;
      const currencyCode = split.credits[0]?.currencyCode;

      const clientId = split.credits[0]?.clientId;

      if (!clientId) {
        throw new Error('ClientId is required but not found in credit');
      }
      if (!billingAccountId) {
        throw new Error('BillingAccountId is required but not found in credit');
      }
      if (!currencyCode) {
        throw new Error('Currency code is required but not found in credit data');
      }

      const creditNote: Partial<ICreditNote> = {
        billingAccountId,
        billingAddress: split.billingAddress,
        clientId,
        ownershipGroupId: split.ownershipGroupId,
        noteDate: makeYmd(new Date()), // Format as YYYY-MM-DD string
        state: EHydraCreditNoteState.unpublished,
        notes: split.notes ?? null,
        credits: split.credits,
        currencyCode,
        subtotalAmount: split.summary.subtotalAmount,
        taxAmount: split.summary.taxAmount,
        roundingAdjustment: split.summary.roundingAdjustment,
        taxName: split.taxName,
        taxRate: split.taxRate,
      };

      // Use the existing saveCreditNote function to create each credit note
      await saveCreditNote(creditNote as ICreditNote);

      // Get the created credit note (saveCreditNote updates the current credit note in state)
      const currentState = store.getState();
      const createdCreditNote = currentState.creditNotes.currentCreditNote;

      if (createdCreditNote?.id) {
        createdCreditNotes.push(createdCreditNote);
      }
    }

    dispatch(setLoadedState('loaded'));

    showMessage({
      message: `Successfully created ${splits.length} credit note(s) in unpublished status`,
      severity: 'success',
    });

    // Reload the credit notes list
    await loadCreditNotes({});

  } catch (error) {
    dispatch(setLoadedState('error'));
    console.error('Failed to create credit notes from splits:', error);
    throw error;
  }
};

/**
 * Uploads a mass credit CSV for validation and returns the draft credit notes it *would*
 * create. This is a dry run: the server writes nothing in response to it.
 *
 * @param csvFile - The CSV file chosen by the user.
 * @param period - The charge period being credited, as `YYYY-MM`. Required by the server: it
 *   is what links each credit to the invoice it comes off.
 * @returns The plan to review, plus any notes this exact CSV has already created.
 */
export const previewMassCreditNotes = async (
  csvFile: File,
  period: string,
): Promise<IMassCreditPreviewResponse> => {
  const formData = new FormData();
  formData.append('action', 'previewMassCreditNotes');
  // A multipart body can only carry text, so the action's parameters travel as JSON and the
  // actions router decodes them (`routers/actions.ts`).
  formData.append('parameters', JSON.stringify({ period }));
  formData.append(MASS_CREDIT_UPLOAD_FIELD, csvFile);

  const url = `${REACT_APP_API_ROOT_URI}/actions`;
  const response = await typedApiFetch<IApiQuerySingularResponse<TActionResponse<IMassCreditPreviewResponse>>>(
    url,
    { method: 'POST', body: formData },
    { defaultJsonContent: false },
  );

  const responseData = await response.json();
  return responseData.data.actionResponse;
};

/**
 * Creates the reviewed mass credit CSV's draft credit notes. The server re-derives
 * everything from `csvText`, so what gets created is what was reviewed.
 *
 * Nothing on this path publishes or applies anything.
 *
 * @param options.csvText - The normalised CSV text returned by the preview.
 * @param options.batchKey - The batch key of the reviewed plan.
 * @param options.period - The credited period the plan was previewed with, as `YYYY-MM`. The
 *   server rebuilds the plan, so this must match or the created notes will not be the ones
 *   that were reviewed.
 * @param options.force - Create again even if this CSV has already produced notes.
 * @returns The created notes, or an error status with a message to display.
 */
export const createMassDraftCreditNotes = async (
  { csvText, batchKey, period, force = false }: {
    csvText: string,
    batchKey: string,
    period: string,
    force?: boolean,
  },
): Promise<IMassCreditCreateResponse> => {
  const responseData = await performApiActionRequest<TActionResponse<IMassCreditCreateResponse>>(
    'createMassDraftCreditNotes',
    { csvText, batchKey, period, force },
  );

  if ('error' in responseData) {
    // `IApiErrorResponse.error` may carry several messages.
    const errorMessage = (
      Array.isArray(responseData.error) ? responseData.error.join(', ') : responseData.error
    );

    return {
      status: 'error',
      message: errorMessage ?? 'Failed to create draft credit notes.',
      createdNotes: [],
    };
  }

  const [singularData] = arrayWrap(responseData.data);
  return singularData.actionResponse;
};

export const publishCreditNotes = async (
  creditNoteIds: number[],
  { shouldBroadcast = false }: { shouldBroadcast?: boolean } = {},
) => {
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes/publish`;
  dispatch(setLoadedState('loading'));
  try {
    const response = await typedApiFetch<IApiCommandResponse<CreditNote>>(url, {
      method: 'POST',
      body: JSON.stringify({ creditNoteIds, shouldBroadcast }),
    });
    if (!response.ok) {
      throw new Error('Error while publishing Credit notes');
    }
    dispatch(setLoadedState('loaded'));
    showMessage({ message: 'Credit Notes published successfully', severity: 'success' });
    return true;
  } catch (error) {
    dispatch(setLoadedState('error'));
    showMessage({ message: 'Failed to publish Credit Notes', severity: 'error' });
    return false;
  }
};

export const deleteCreditNotes = async (creditNoteIds: number[]) => {
  const url = `${REACT_APP_API_ROOT_URI}/credit_notes/delete`;
  dispatch(setLoadedState('loading'));
  try {
    const response = await typedApiFetch<IApiCommandResponse<{ affectedRows?: number }>>(url, {
      method: 'POST',
      body: JSON.stringify({creditNoteIds}),
    });
    if (!response.ok) {
      throw new Error('Error while deleting Credit notes');
    }
    dispatch(setLoadedState('loaded'));
    showMessage({ message: 'Credit Notes deleted successfully', severity: 'success' });
    // Reload the credit notes list to reflect the changes
    await loadCreditNotes({});
  } catch (error) {
    dispatch(setLoadedState('error'));
    showMessage({ message: 'Failed to delete Credit Notes', severity: 'error' });
  }
};
